import React, { useEffect, useState } from "react";
import PartnerLayout, { tenantSlugFor } from "@/components/PartnerLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Plus, X, Download } from "lucide-react";

export default function Licencias() {
  const { user } = useAuth();
  const slug = tenantSlugFor(user);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ status: "", search: "" });
  const [showModal, setShowModal] = useState(false);

  const load = () => {
    const p = new URLSearchParams();
    if (filter.status) p.append("status", filter.status);
    if (filter.search) p.append("search", filter.search);
    p.append("limit", 100);
    api.get(`/partner/${slug}/licenses?${p}`).then((r) => setItems(r.data.items));
  };
  useEffect(() => { load(); }, [slug, filter.status, filter.search]);

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta licencia?")) return;
    try {
      await api.post(`/partner/${slug}/licenses/${id}/revoke`);
      toast.success("Licencia revocada"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PartnerLayout user={user}>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Licencias</p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">Gestión de licencias</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowModal("batch")} data-testid="btn-new-batch"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> Generar lote
            </button>
            <button onClick={() => setShowModal("csv")} data-testid="btn-upload-csv"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-gray-700 text-white text-sm font-semibold hover:bg-white/5">
              <Upload className="w-4 h-4" /> Subir CSV
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          <input placeholder="Buscar código o email..." data-testid="filter-search"
            value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            className="bg-[#111827] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:border-[#3B82F6] outline-none" />
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            data-testid="filter-status"
            className="bg-[#111827] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
            <option value="">Todos los estados</option>
            <option value="available">Disponibles</option>
            <option value="assigned">Activadas</option>
            <option value="revoked">Revocadas</option>
          </select>
        </div>

        <div className="overflow-x-auto border border-gray-800 rounded-xl">
          <table className="w-full text-sm" data-testid="licenses-table">
            <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">Programa</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-left px-4 py-3">Email pre-asig.</th>
                <th className="text-left px-4 py-3">Activada</th>
                <th className="text-right px-4 py-3">Acción</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-t border-gray-800 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-mono text-white">{l.code}</td>
                  <td className="px-4 py-3 text-gray-300">{l.program_title}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${
                      l.status === "assigned" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" :
                      l.status === "revoked" ? "bg-red-500/10 text-red-300 border-red-500/30" :
                      "bg-brand-orange-500/10 text-brand-orange-300 border-primary/30"
                    }`}>{l.status}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-400">{l.email_pre_assigned || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {l.activated_at ? new Date(l.activated_at).toLocaleDateString("es-MX") : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {l.status !== "revoked" && (
                      <button onClick={() => revoke(l.id)} data-testid={`revoke-${l.id}`}
                        className="text-xs text-red-300 hover:text-red-100">Revocar</button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Sin licencias.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal === "batch" && <BatchModal slug={slug} onClose={() => { setShowModal(false); load(); }} />}
      {showModal === "csv" && <CsvModal slug={slug} onClose={() => { setShowModal(false); load(); }} />}
    </PartnerLayout>
  );
}

function BatchModal({ slug, onClose }) {
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({ program_id: "", quantity: 10 });
  const [codes, setCodes] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/catalog/programs?limit=100").then((r) => setPrograms(r.data.items));
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const r = await api.post(`/partner/${slug}/licenses/batch`, form);
      setCodes(r.data);
      toast.success(`${r.data.quantity} códigos generados`);
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const downloadCsv = () => {
    const csv = "code\n" + codes.codes.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `licencias-${slug}-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4" data-testid="batch-modal">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-semibold text-white">Generar lote</h3>
          <button onClick={onClose} className="text-gray-400"><X /></button>
        </div>
        {!codes ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400">Programa</label>
              <select data-testid="batch-program" required value={form.program_id} onChange={(e) => setForm({ ...form, program_id: e.target.value })}
                className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
                <option value="">Selecciona...</option>
                {programs.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.title}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400">Cantidad</label>
              <input data-testid="batch-quantity" type="number" min="1" max="1000" value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || "1") })}
                className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
            </div>
            <button type="submit" disabled={submitting} data-testid="batch-submit"
              className="w-full py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold disabled:opacity-60">
              {submitting ? "Generando..." : "Generar códigos"}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <p className="text-emerald-300 font-semibold">{codes.quantity} códigos generados</p>
            <div className="max-h-48 overflow-y-auto border border-gray-800 rounded p-3 bg-[#0B0F14] font-mono text-xs" data-testid="batch-codes">
              {codes.codes.map((c) => <div key={c} className="text-white">{c}</div>)}
            </div>
            <button onClick={downloadCsv} data-testid="batch-download-csv"
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold">
              <Download className="w-4 h-4" /> Descargar CSV
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CsvModal({ slug, onClose }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/partner/${slug}/licenses/csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setResult(r.data);
      toast.success(`${r.data.created} licencias creadas`);
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4" data-testid="csv-modal">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-semibold text-white">Subir CSV pre-asignado</h3>
          <button onClick={onClose} className="text-gray-400"><X /></button>
        </div>
        <p className="text-xs text-gray-400">CSV con columnas: <code className="font-mono">email, full_name, program_code</code>. Cada fila crea una licencia asignada a ese email y envía email de bienvenida (queda en Outbox).</p>
        {!result ? (
          <form onSubmit={submit} className="space-y-4">
            <input type="file" accept=".csv" required data-testid="csv-file"
              onChange={(e) => setFile(e.target.files[0])}
              className="text-sm text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:rounded file:bg-[#B84A0C] file:text-white file:border-0" />
            <button type="submit" disabled={submitting || !file} data-testid="csv-submit"
              className="w-full py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold disabled:opacity-60">
              {submitting ? "Procesando..." : "Subir y crear licencias"}
            </button>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-emerald-300 font-semibold">✓ {result.created} licencias creadas</p>
            {result.failed > 0 && <p className="text-red-300">✗ {result.failed} fallaron</p>}
            {result.errors?.length > 0 && (
              <div className="max-h-40 overflow-y-auto border border-red-500/30 rounded p-3 bg-red-500/5 text-xs">
                {result.errors.map((e, i) => <div key={i} className="text-red-300">{e.email}: {e.error}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
